import { computeEpaCorrectionDeadline } from "encamp-shared/src/waste/epaCorrectionRequests";
import {
  ContainerFindingKind,
  DiscrepancyKind,
  DiscrepancySeverity,
  FindingEntity,
  FindingSeverity,
  ManifestComplianceEscalation,
  WasteComplianceFindingsQuery,
} from "generated-graphql/graphql";

type TenantFindings = NonNullable<WasteComplianceFindingsQuery["tenant"]>;
export type ContainerFindingRow = TenantFindings["containerComplianceFindings"]["items"][number];
export type ManifestFindingRow = TenantFindings["manifestComplianceFindings"]["items"][number];

/** Which system produced the finding — drives the source tag on the row. */
export type FeedSource = "container" | "manifest";

/**
 * The filter groups the dashboard offers. Coarser than the finding kinds
 * themselves: users think in terms of the problem area, not the eight kinds.
 */
export type FeedGroup = "Accumulation" | "Profile" | "Episodic" | "Manifest";

export const FEED_GROUPS: readonly FeedGroup[] = [
  "Accumulation",
  "Profile",
  "Episodic",
  "Manifest",
];

/** One row of the merged feed, normalized from either source. */
export type ComplianceFeedItem = {
  id: string;
  source: FeedSource;
  group: FeedGroup;
  severity: FindingSeverity;
  /** Small overline above the title, naming the condition. */
  kindLabel: string;
  /**
   * What the row is about, for the tag under the deadline: the record scope for
   * accumulation findings (Container / Area / Facility), or the problem area
   * where that reads better (Profile / Episodic / Manifest).
   */
  categoryLabel: string;
  title: string;
  detail: string;
  facilityId: string | null;
  facilityName: string | null;
  /**
   * Drives the urgency pill. Null for conditions that are not time-based (the
   * on-site threshold findings) and for missing-e-Manifest rows, which escalate by
   * stage rather than against a date.
   */
  deadline: Date | null;
  /**
   * What the urgency chip says when there is no deadline to count down to.
   * Describes the state itself ("Over limit", "Incomplete") rather than
   * restating severity, which told the reader nothing actionable.
   */
  undatedLabel: string;
  /**
   * A missed deadline that carries no date — only the missing-e-Manifest rows
   * escalated past 60 days. Set from the source kind rather than inferred from
   * severity, which would also catch the undated on-site threshold breaches.
   */
  overdueWithoutDeadline: boolean;
  /** In-app path to the record that resolves it, or null when there is none. */
  linkTo: string | null;
};

const CONTAINER_KIND_LABELS: Record<ContainerFindingKind, string> = {
  [ContainerFindingKind.AccumulationOverdue]: "Accumulation overdue",
  [ContainerFindingKind.AccumulationApproaching]: "Accumulation approaching",
  [ContainerFindingKind.SatelliteOverThreshold]: "Satellite area over limit",
  [ContainerFindingKind.OnsiteAccumulationThresholdExceeded]:
    "On-site accumulation threshold exceeded",
  [ContainerFindingKind.OnsiteAccumulationThresholdApproaching]:
    "Approaching on-site accumulation threshold",
  [ContainerFindingKind.EpisodicExpiredOnHand]: "Episodic window expired",
  [ContainerFindingKind.ProfileRetestOverdue]: "Profile re-test overdue",
  [ContainerFindingKind.ProfileIncomplete]: "Profile incomplete",
};

const CONTAINER_KIND_GROUPS: Record<ContainerFindingKind, FeedGroup> = {
  [ContainerFindingKind.AccumulationOverdue]: "Accumulation",
  [ContainerFindingKind.AccumulationApproaching]: "Accumulation",
  [ContainerFindingKind.SatelliteOverThreshold]: "Accumulation",
  [ContainerFindingKind.OnsiteAccumulationThresholdExceeded]: "Accumulation",
  [ContainerFindingKind.OnsiteAccumulationThresholdApproaching]: "Accumulation",
  [ContainerFindingKind.EpisodicExpiredOnHand]: "Episodic",
  [ContainerFindingKind.ProfileRetestOverdue]: "Profile",
  [ContainerFindingKind.ProfileIncomplete]: "Profile",
};

const CONTAINER_UNDATED_LABELS: Record<ContainerFindingKind, string> = {
  [ContainerFindingKind.AccumulationOverdue]: "Overdue",
  [ContainerFindingKind.AccumulationApproaching]: "Due soon",
  [ContainerFindingKind.SatelliteOverThreshold]: "Over limit",
  [ContainerFindingKind.OnsiteAccumulationThresholdExceeded]: "Over threshold",
  [ContainerFindingKind.OnsiteAccumulationThresholdApproaching]: "Near threshold",
  [ContainerFindingKind.EpisodicExpiredOnHand]: "Window expired",
  [ContainerFindingKind.ProfileRetestOverdue]: "Overdue",
  [ContainerFindingKind.ProfileIncomplete]: "Incomplete",
};

const MANIFEST_KIND_LABELS: Record<DiscrepancyKind, string> = {
  [DiscrepancyKind.EmanifestMissing]: "Missing e-Manifest",
  [DiscrepancyKind.EpaCorrectionRequest]: "EPA correction requested",
  [DiscrepancyKind.FieldDiscrepancy]: "Data source conflict",
  // Staff-only; the resolver never returns these, so it is unreachable here.
  [DiscrepancyKind.FieldIssue]: "Data quality issue",
};

/** The manifest severity vocabulary mapped onto the findings one. */
const MANIFEST_SEVERITY: Record<DiscrepancySeverity, FindingSeverity> = {
  [DiscrepancySeverity.Critical]: FindingSeverity.High,
  [DiscrepancySeverity.Moderate]: FindingSeverity.Medium,
  [DiscrepancySeverity.Minor]: FindingSeverity.Low,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const ENTITY_LABELS: Record<FindingEntity, string> = {
  [FindingEntity.Container]: "Container",
  [FindingEntity.Area]: "Area",
  [FindingEntity.Facility]: "Facility",
  [FindingEntity.WasteStream]: "Waste stream",
};

/**
 * Accumulation findings are best identified by the record they sit on, since
 * "Accumulation" is already implied by the kind above; the profile and episodic
 * groups read better as the problem area than as "Waste stream".
 */
function containerCategoryLabel(finding: ContainerFindingRow): string {
  const group = CONTAINER_KIND_GROUPS[finding.kind];
  return group === "Accumulation" ? ENTITY_LABELS[finding.entity] : group;
}

export function containerFindingLink(
  tenantId: string,
  finding: Pick<
    ContainerFindingRow,
    "entity" | "containerId" | "areaId" | "wasteStreamId" | "facilityId"
  >
): string | null {
  const base = `/o/${tenantId}/waste`;
  switch (finding.entity) {
    case FindingEntity.Container:
      return finding.containerId ? `${base}/containers/${finding.containerId}` : null;
    case FindingEntity.Area:
      return finding.areaId ? `${base}/accumulation-areas/${finding.areaId}` : null;
    case FindingEntity.WasteStream:
      return finding.wasteStreamId ? `${base}/streams/${finding.wasteStreamId}` : null;
    case FindingEntity.Facility:
      return `${base}/facilities/${finding.facilityId}`;
    default:
      return null;
  }
}

function manifestFindingLink(tenantId: string, row: ManifestFindingRow): string | null {
  // Opens the manifest the finding belongs to; the drawer there carries the
  // discrepancy review surface.
  return row.processedManifestId
    ? `/o/${tenantId}/waste/manifests/${row.processedManifestId}`
    : null;
}

/**
 * Manifest rows carry a real date only for EPA corrections (request + 30 days).
 * Missing-e-Manifest rows escalate by stage (`OVERDUE_45` / `OVERDUE_60`) with no
 * date to count down to, and field conflicts have no deadline at all.
 */
function manifestDeadline(row: ManifestFindingRow): Date | null {
  const requestDate = row.wasteManifestCorrectionRequest?.requestDate;
  if (row.kind !== DiscrepancyKind.EpaCorrectionRequest || !requestDate) {
    return null;
  }
  const requested = new Date(requestDate);
  if (Number.isNaN(requested.getTime())) {
    return null;
  }
  return computeEpaCorrectionDeadline(requested);
}

/** One definition of "escalated", shared by the copy, the chip and the KPI. */
function isEscalatedMissingEManifest(row: ManifestFindingRow): boolean {
  return (
    row.kind === DiscrepancyKind.EmanifestMissing &&
    row.escalation === ManifestComplianceEscalation.Overdue_60
  );
}

function manifestDetail(row: ManifestFindingRow): string {
  const where = row.facilityName ?? row.epaId ?? "Unknown facility";
  switch (row.kind) {
    case DiscrepancyKind.EmanifestMissing:
      return isEscalatedMissingEManifest(row)
        ? `No e-Manifest 60+ days after shipment — ${where}.`
        : `No e-Manifest 45+ days after shipment — ${where}.`;
    case DiscrepancyKind.EpaCorrectionRequest:
      return `EPA requested a correction on this manifest — ${where}.`;
    default:
      return `Sources disagree on ${row.field} — ${where}.`;
  }
}

function manifestUndatedLabel(row: ManifestFindingRow): string {
  switch (row.kind) {
    case DiscrepancyKind.EmanifestMissing:
      return isEscalatedMissingEManifest(row) ? "60+ days overdue" : "45+ days overdue";
    case DiscrepancyKind.EpaCorrectionRequest:
      return "Response needed";
    default:
      return "Needs review";
  }
}

export function toFeedItems(
  tenantId: string,
  containerFindings: readonly ContainerFindingRow[],
  manifestFindings: readonly ManifestFindingRow[]
): ComplianceFeedItem[] {
  const containerItems = containerFindings.map((finding) => ({
    id: finding.id,
    source: "container" as const,
    group: CONTAINER_KIND_GROUPS[finding.kind],
    severity: finding.severity,
    kindLabel: CONTAINER_KIND_LABELS[finding.kind],
    categoryLabel: containerCategoryLabel(finding),
    undatedLabel: CONTAINER_UNDATED_LABELS[finding.kind],
    // The accumulation kinds carry deadlines; the threshold kinds are quantity breaches.
    overdueWithoutDeadline: false,
    title: finding.entityLabel,
    detail: finding.detail,
    facilityId: finding.facilityId,
    facilityName: null,
    deadline: finding.deadline ? new Date(finding.deadline) : null,
    linkTo: containerFindingLink(tenantId, finding),
  }));

  const manifestItems = manifestFindings.map((row) => ({
    id: row.id,
    source: "manifest" as const,
    group: "Manifest" as const,
    severity: MANIFEST_SEVERITY[row.severity],
    kindLabel: MANIFEST_KIND_LABELS[row.kind],
    categoryLabel: "Manifest",
    undatedLabel: manifestUndatedLabel(row),
    overdueWithoutDeadline: isEscalatedMissingEManifest(row),
    title: row.manifestTrackingNumber ?? "Manifest",
    detail: manifestDetail(row),
    facilityId: row.facilityId ?? null,
    facilityName: row.facilityName ?? null,
    deadline: manifestDeadline(row),
    linkTo: manifestFindingLink(tenantId, row),
  }));

  return sortByUrgency([...containerItems, ...manifestItems]);
}

const SEVERITY_RANK: Record<FindingSeverity, number> = {
  [FindingSeverity.High]: 0,
  [FindingSeverity.Medium]: 1,
  [FindingSeverity.Low]: 2,
};

/**
 * Most severe first, then soonest/most-overdue deadline, with undated rows last
 * within their severity. Id breaks ties so the order never depends on which
 * source happened to load first.
 */
export function sortByUrgency(items: readonly ComplianceFeedItem[]): ComplianceFeedItem[] {
  return [...items].sort((a, b) => {
    const bySeverity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (bySeverity !== 0) return bySeverity;
    if (a.deadline && b.deadline) {
      const byDeadline = a.deadline.getTime() - b.deadline.getTime();
      if (byDeadline !== 0) return byDeadline;
    } else if (a.deadline) {
      return -1;
    } else if (b.deadline) {
      return 1;
    }
    return a.id.localeCompare(b.id);
  });
}

/** Both sources are paged, so the totals come from their counts, not the rows. */
export type FeedTotals = { containerTotal: number; manifestTotal: number };

export type ComplianceKpis = {
  openFindings: number;
  /**
   * These three can only describe the loaded rows — the backend returns totals,
   * not breakdowns — so past one page they are a floor. Survivable because each
   * page is ordered most-urgent-first.
   */
  overdue: number;
  dueWithinSevenDays: number;
  facilitiesAtRisk: number;
  /**
   * Findings that came from container tracking rather than from manifests —
   * accumulation, on-site threshold, profile and episodic alike. Provenance, not
   * scope: a profile finding counts here because the container-tracking system
   * produced it.
   */
  containerTrackingFindings: number;
};

/** Whole days until the deadline; negative once past. */
export function daysUntil(deadline: Date, now: Date): number {
  const toUtcMidnight = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round((toUtcMidnight(deadline) - toUtcMidnight(now)) / MS_PER_DAY);
}

/**
 * The five headline numbers. `overdue` takes dated rows whose deadline has
 * passed plus undated ones that are overdue anyway; `dueWithinSevenDays` can
 * only cover dated rows. `totals` come from the backend, which `items` cannot
 * supply because it holds at most one page of each source.
 */
export function computeKpis(
  items: readonly ComplianceFeedItem[],
  now: Date,
  totals: FeedTotals
): ComplianceKpis {
  let overdue = 0;
  let dueWithinSevenDays = 0;
  const facilities = new Set<string>();

  for (const item of items) {
    if (item.facilityId) facilities.add(item.facilityId);

    if (item.deadline) {
      const days = daysUntil(item.deadline, now);
      if (days < 0) overdue++;
      else if (days <= 7) dueWithinSevenDays++;
    } else if (item.overdueWithoutDeadline) {
      overdue++;
    }
  }

  return {
    openFindings: totals.containerTotal + totals.manifestTotal,
    overdue,
    dueWithinSevenDays,
    facilitiesAtRisk: facilities.size,
    containerTrackingFindings: totals.containerTotal,
  };
}

/** Type-filter option: one finding kind present in the feed, with its count. */
export type KindFilterOption = { label: string; count: number };

/** The per-kind totals each source reports, before any kind filter is applied. */
export type KindCounts = {
  container: readonly { kind: ContainerFindingKind; count: number }[];
  manifest: readonly { kind: DiscrepancyKind; count: number }[];
};

/**
 * The kinds actually present, most common first. Built from the servers' unpaged
 * counts rather than from the loaded rows: with the feed paged, counting what
 * arrived would under-report, and offering a kind whose findings all sit on a
 * later page would filter to an empty list.
 */
export function kindFilterOptions(counts: KindCounts): KindFilterOption[] {
  const byLabel = new Map<string, number>();
  const add = (label: string, count: number) =>
    byLabel.set(label, (byLabel.get(label) ?? 0) + count);

  for (const { kind, count } of counts.container) add(CONTAINER_KIND_LABELS[kind], count);
  for (const { kind, count } of counts.manifest) add(MANIFEST_KIND_LABELS[kind], count);

  return [...byLabel.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * The server filters a selected chip resolves to. A label belongs to exactly one
 * source, so the other gets an empty list — meaning "ask for nothing", which is
 * why the resolvers treat empty and absent differently.
 */
export type KindSelection = {
  containerKinds: ContainerFindingKind[];
  manifestKinds: DiscrepancyKind[];
};

const ALL_CONTAINER_KINDS = Object.keys(CONTAINER_KIND_LABELS) as ContainerFindingKind[];
const CUSTOMER_MANIFEST_KINDS = [
  DiscrepancyKind.EmanifestMissing,
  DiscrepancyKind.EpaCorrectionRequest,
  DiscrepancyKind.FieldDiscrepancy,
];

/** Resolves a chip label (null = All) to the kinds each source should return. */
export function kindsForLabel(label: string | null): KindSelection {
  if (label === null) {
    return { containerKinds: ALL_CONTAINER_KINDS, manifestKinds: CUSTOMER_MANIFEST_KINDS };
  }
  return {
    containerKinds: ALL_CONTAINER_KINDS.filter((kind) => CONTAINER_KIND_LABELS[kind] === label),
    manifestKinds: CUSTOMER_MANIFEST_KINDS.filter((kind) => MANIFEST_KIND_LABELS[kind] === label),
  };
}

export type FeedSortKey = "urgency" | "deadline" | "type" | "kind";

export const FEED_SORTS: readonly { key: FeedSortKey; label: string }[] = [
  { key: "urgency", label: "Urgency" },
  { key: "deadline", label: "Deadline (soonest)" },
  { key: "type", label: "Type" },
  { key: "kind", label: "Scope" },
];

/**
 * Every sort ends with the urgency comparator as its tiebreaker, so equal keys
 * still land in a stable, meaningful order rather than whatever order the two
 * sources happened to load in.
 */
export function sortFeed(
  items: readonly ComplianceFeedItem[],
  key: FeedSortKey
): ComplianceFeedItem[] {
  const byUrgency = sortByUrgency(items);
  switch (key) {
    case "urgency":
      return byUrgency;
    case "deadline":
      // Undated rows last; they have nothing to count down to.
      return [...byUrgency].sort((a, b) => {
        if (a.deadline && b.deadline) return a.deadline.getTime() - b.deadline.getTime();
        if (a.deadline) return -1;
        if (b.deadline) return 1;
        return 0;
      });
    case "type":
      return [...byUrgency].sort((a, b) => a.kindLabel.localeCompare(b.kindLabel));
    case "kind":
      return [...byUrgency].sort((a, b) => a.categoryLabel.localeCompare(b.categoryLabel));
  }
}
